/**
 * Composant de configuration Telegram pour les notifications admin
 * Bot centralisé côté serveur - l'utilisateur n'a qu'à lier ses admins
 * 2 sections : Liaison Admin 1, Liaison Admin 2
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, message, Space, Divider, Tag, Typography, Alert, Spin } from 'antd';
import { SendOutlined, LinkOutlined, DisconnectOutlined, CheckCircleOutlined, CloseCircleOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { telegramApi } from '../../utils/api';

const { Text, Title, Paragraph } = Typography;

const TelegramSettings = () => {
  // État de la config
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // État des codes de liaison (code, instruction et lien direct Telegram)
  const [linkCode1, setLinkCode1] = useState(null);
  const [linkCode2, setLinkCode2] = useState(null);
  const [linkInstruction1, setLinkInstruction1] = useState('');
  const [linkInstruction2, setLinkInstruction2] = useState('');
  const [deepLink1, setDeepLink1] = useState(null);
  const [deepLink2, setDeepLink2] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(null); // 'admin1' ou 'admin2'
  const [testing, setTesting] = useState(null); // 'admin1', 'admin2' ou 'both'
  const [unlinking, setUnlinking] = useState(null); // 'admin1' ou 'admin2'

  /**
   * Charge la configuration Telegram depuis l'API
   */
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await telegramApi.getConfig();
      setConfig(response.data.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erreur chargement config Telegram:', error);
      }
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /**
   * Génère un code de liaison pour un admin
   * @param {string} adminSlot - 'admin1' ou 'admin2'
   */
  const handleGenerateCode = async (adminSlot) => {
    try {
      setGeneratingCode(adminSlot);
      const response = await telegramApi.generateLinkCode(adminSlot);
      const { code, instruction, deepLink } = response.data;

      // Stocker code, instruction et deep link pour l'admin concerné
      if (adminSlot === 'admin1') {
        setLinkCode1(code);
        setLinkInstruction1(instruction);
        setDeepLink1(deepLink);
      } else {
        setLinkCode2(code);
        setLinkInstruction2(instruction);
        setDeepLink2(deepLink);
      }

      message.success('Code généré ! Valide pendant 15 minutes.');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de la génération du code';
      message.error(errorMsg);
    } finally {
      setGeneratingCode(null);
    }
  };

  /**
   * Délie un admin de Telegram
   * @param {string} adminSlot - 'admin1' ou 'admin2'
   */
  const handleUnlink = async (adminSlot) => {
    try {
      setUnlinking(adminSlot);
      await telegramApi.unlinkAdmin(adminSlot);
      message.success(`${adminSlot === 'admin1' ? 'Admin 1' : 'Admin 2'} délié`);
      await loadConfig();
    } catch (error) {
      message.error('Erreur lors de la suppression de la liaison');
    } finally {
      setUnlinking(null);
    }
  };

  /**
   * Envoie une notification de test
   * @param {string} adminSlot - 'admin1', 'admin2' ou 'both'
   */
  const handleTest = async (adminSlot) => {
    try {
      setTesting(adminSlot);
      const response = await telegramApi.testNotification(adminSlot);
      if (response.data.success) {
        message.success('Message de test envoyé !');
      } else {
        message.warning('Test envoyé avec des erreurs. Vérifiez la configuration.');
      }
    } catch (error) {
      message.error('Erreur lors de l\'envoi du test');
    } finally {
      setTesting(null);
    }
  };

  /**
   * Copie un texte dans le presse-papiers
   * @param {string} text - Texte à copier
   */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('Copié !');
    }).catch(() => {
      message.error('Erreur de copie');
    });
  };

  /**
   * Rendu d'une section de liaison admin
   * @param {string} slot - 'admin1' ou 'admin2'
   * @param {boolean} isLinked - Si l'admin est déjà lié
   * @param {string} chatId - Chat ID masqué
   * @param {string} linkCode - Code de liaison actuel
   * @param {string} linkInstruction - Instruction de liaison
   */
  const renderAdminSection = (slot, isLinked, chatId, linkCode, linkInstruction, deepLink) => {
    const label = slot === 'admin1' ? 'Admin 1' : 'Admin 2';

    return (
      <div style={{ marginBottom: 16 }}>
        <Space align="center" style={{ marginBottom: 8 }}>
          <Text strong>{label}</Text>
          {isLinked ? (
            <Tag icon={<CheckCircleOutlined />} color="success">Lié ({chatId})</Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">Non lié</Tag>
          )}
        </Space>

        <div style={{ marginLeft: 8 }}>
          {/* Boutons d'action */}
          <Space wrap>
            {!isLinked && (
              <Button
                icon={<LinkOutlined />}
                onClick={() => handleGenerateCode(slot)}
                loading={generatingCode === slot}
                type="primary"
                ghost
              >
                Générer code de liaison
              </Button>
            )}

            {isLinked && (
              <>
                <Button
                  icon={<SendOutlined />}
                  onClick={() => handleTest(slot)}
                  loading={testing === slot}
                >
                  Tester
                </Button>
                <Button
                  icon={<DisconnectOutlined />}
                  onClick={() => handleUnlink(slot)}
                  loading={unlinking === slot}
                  danger
                >
                  Délier
                </Button>
              </>
            )}
          </Space>

          {/* Instruction de liaison après génération du code */}
          {linkCode && !isLinked && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message={`Code de liaison: ${linkCode}`}
              description={
                <div>
                  <Paragraph style={{ marginBottom: 8 }}>{linkInstruction}</Paragraph>
                  <Space wrap>
                    {/* Bouton principal : ouvre Telegram directement si deep link disponible */}
                    {deepLink && (
                      <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        href={deepLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ouvrir dans Telegram
                      </Button>
                    )}
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(`/start ${linkCode}`)}
                    >
                      Copier la commande
                    </Button>
                  </Space>
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Expire dans 15 minutes. Actualisez la page après la liaison.
                  </Text>
                </div>
              }
            />
          )}
        </div>
      </div>
    );
  };

  // Affichage du chargement
  if (loading) {
    return (
      <Card title={<><SendOutlined /> Notifications Telegram</>}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card title={<><SendOutlined /> Notifications Telegram</>}>
      <Alert
        message="Les notifications admin (nouvelles commandes, annulations, alertes stock, etc.) sont envoyées via Telegram."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Vérifier si le bot est configuré côté serveur */}
      {config && !config.botConfigured && (
        <Alert
          message="Le bot Telegram n'est pas encore configuré sur le serveur. Contactez l'administrateur."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Sections liaison des Admins */}
      <Title level={5}>Liaison des comptes Telegram</Title>
      <Paragraph type="secondary">
        Chaque administrateur doit lier son compte Telegram pour recevoir les notifications.
      </Paragraph>

      {!config?.botConfigured ? (
        <Alert message="Bot Telegram non disponible. Contactez l'administrateur." type="warning" showIcon />
      ) : (
        <>
          {renderAdminSection(
            'admin1',
            config?.admin1Linked,
            config?.admin1ChatId,
            linkCode1,
            linkInstruction1,
            deepLink1
          )}

          <Divider dashed />

          {renderAdminSection(
            'admin2',
            config?.admin2Linked,
            config?.admin2ChatId,
            linkCode2,
            linkInstruction2,
            deepLink2
          )}

          <Divider dashed />

          {/* Boutons globaux */}
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadConfig}
              loading={loading}
            >
              Actualiser
            </Button>
            {(config?.admin1Linked || config?.admin2Linked) && (
              <Button
                icon={<SendOutlined />}
                onClick={() => handleTest('both')}
                loading={testing === 'both'}
                type="primary"
              >
                Tester tous les admins
              </Button>
            )}
          </Space>
        </>
      )}
    </Card>
  );
};

export default TelegramSettings;
